import { AppDate } from "@app/util/app-date";
import { todo } from "@app/util/todo";
import { DateObject } from "../models/DateObject";
import { CalendarApi } from "./apiServices/CalendarApi";
import { Inject, Injectable } from "@angular/core";
import { AppDataService } from "@app/services/appData.service";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";

/**
 * PaymentDateService assists in payment date logic such as getting
 * the next proocessing day and cutoff hour considerations.
 */
@Injectable({ providedIn: "root" })
export class PaymentDateService {
    constructor(@Inject(CalendarApi) private calendarApi: CalendarApi,
                @Inject(AppDataService) private appService) {

        if (PaymentDateService.hour == null || PaymentDateService.minute == null || PaymentDateService.today == null) {
            PaymentDateService.updateTimes()
            // Update cached times if not yet present
        }

        if (PaymentDateService.holidays == null) {
            const currentYear = PaymentDateService.today.year;
            PaymentDateService.updateHolidays(calendarApi, [currentYear])
            // Update cached holidays if not yet present
        }
    }

    /** Convert the date to the DateObject */
    dateObjectFromAppDate = PaymentDateService.dateObjectFromAppDate
    /** Convert the AppDate to the DateObject, returns null if null */
    static dateObjectFromAppDate(date: AppDate): DateObject {
        if (date instanceof AppDate) {
            return {
                day: date.date,
                month: date.month + 1,
                year: date.year,
                /** "MM/dd/yyyy" */
                formattedFullDate: date.format("MM/dd/yyyy")
            }
        }

        return null
    }

    /** AppDate indexes */
    private static holidays: Set<number>
    private static holidayYears: Set<number>
    private static today: AppDate
    private static hour: number
    private static minute: number

    // Map of year to promise of holiday data
    private static holidayRequests = new Map<number, Promise<boolean>>();

    delayTodaysPayment: boolean = false;

    /** You may clear this timeout for testing purposes */
    protected static updateTimesInterval = setInterval(() => PaymentDateService.updateTimes(), 30 * 1000)

    /** Configurable by clearing the updateTodayInterval and passing in a date here for testing */
    protected static updateTimes(date = new Date()) {
        let dateNewYorkTZ = new Date(date.toLocaleString('en-us', { timeZone: AppDate.timezone }))
        PaymentDateService.today = AppDate.fromDate(dateNewYorkTZ);
        PaymentDateService.hour = PaymentDateService.today.hour;
        PaymentDateService.minute = PaymentDateService.today.minute;
    }

    /** Configurable by clearing the updateTodayInterval and passing in a date here for testing */
    protected static updateHolidays(calendarApi: CalendarApi, years: number[]): Promise<boolean> {
        this.holidays = this.holidays || new Set()

        const requestsToWaitFor: Promise<boolean>[] = [];
        const uniqueYears = [...new Set(years)];

        for (const year of uniqueYears) {
            if (!this.holidayRequests.has(year)) {
                const newRequest = this.requestHolidayDates(calendarApi, year)
                    .then(holidaysOfYear => {
                        for (const holiday of holidaysOfYear) {
                            this.holidays.add(holiday.index);
                        }
                        return true;
                    })
                    .catch(err => {
                        console.error(`Error updating holidays for ${year}:`, err);
                        this.holidayRequests.delete(year);
                        return false;
                    });
                this.holidayRequests.set(year, newRequest);
            } else {
                console.log(`Skipping request for ${year} as it's already pending.`);
            }

            const promiseToWait = this.holidayRequests.get(year);
            requestsToWaitFor.push(promiseToWait);
        }

        return Promise.all(requestsToWaitFor).then(results => {
            const allSucceeded = results.every(success => success);

            if (allSucceeded) {
                console.log(`All holiday requests for years ${uniqueYears.join(', ')} completed successfully.`);
            } else {
                console.error(`Failed to update holidays for some years: ${uniqueYears.join(', ')}`);
            }

            return allSucceeded;
        });
    }

    private static requestHolidayDates(calendarApi: CalendarApi, year: number): Promise<AppDate[]> {

        return new Promise<AppDate[]>((res, rej) => {
            calendarApi.holidayCalendarGet(true, year)
                .pipe(take(1)).subscribe(resp => {
                /** Format "yyyy-MM-dd" */
                    const holidayData = resp
                    if (holidayData == null) {
                        const msg = 'CalendarApi holidayCalendarGet responded with null data'
                        console.warn(msg, resp)
                        rej(msg)
                    } else {
                        const holidays = holidayData.map(dateString => AppDate.fromString(dateString, "yyyy-MM-dd"))
                        res(holidays)
                    }
                }, () => rej());
        })
    }

    /**
     * Ensure that the years of holidays are present
     * @param years years you'd like to ensure have up-to-date invalid date information (e.g. holidays)
     */
    updateHolidays(years: number[]): Promise<boolean> {
        return PaymentDateService.updateHolidays(this.calendarApi, years);
    }

    /**
     * Check if payment will be processable on this date
     * If your date matches @property {AppDateAjs} today, then the cutoff time will be used
     * This does not take into account maximum days, but uses today as minimum date
     */
    validatePaymentDate(date: AppDate, cutoffHour: number, cutoffMinute: number): PaymentDateValidation {
        return this._validatePmtDate(date, PaymentDateService.hour, cutoffHour, PaymentDateService.minute, cutoffMinute)
    }

    private _validatePmtDate(date: AppDate, hour: number, cutoffHour: number, minute: number, cutoffMinute: number): PaymentDateValidation {
        if (date.year < 1000 || date.year > 9999) return PaymentDateValidation.ERROR
        // don't bother
        /*if (!PaymentDateService.holidayYears.has(date.year - 1)) this.updateHolidays([date.year - 1])
        if (!PaymentDateService.holidayYears.has(date.year + 1)) this.updateHolidays([date.year + 1])
        // preload surrounding years

        if (!PaymentDateService.holidayYears.has(date.year)) {
            this.updateHolidays([date.year])
            return PaymentDateValidation.ERROR
        }*/

        if (date.day === 0 || date.day === 6) {
            // date is weekend
            return PaymentDateValidation.WEEKEND
        }

        if (PaymentDateService.holidays.has(date.index)) {
            // date is a holiday
            console.log(`Date ${date.index} IS A HOLIDAY.}`)
            return PaymentDateValidation.HOLIDAY
        }

        if (date.isBefore(PaymentDateService.today)) {
            // out of range (in the past)
            return PaymentDateValidation.PAST_DATE
        }

        if (date.equals(PaymentDateService.today)) {

            if(this.delayTodaysPayment) {
                console.log("Treating today as a holiday");
                return PaymentDateValidation.HOLIDAY;
            }

            // date is minimum
            if (hour != null && cutoffHour != null && minute != null && cutoffMinute != null) {
                if ((hour > cutoffHour) || (hour === cutoffHour && minute >= cutoffMinute)) {
                    // is today & after cutoff
                    return PaymentDateValidation.PAST_CUTOFF
                }
            }
        }
        // everything else is valid
        return PaymentDateValidation.OK
    }

    getNextValidDate(appDate: AppDate, cutoffHour: number, cutoffMinute: number, max: AppDate = appDate.addDays(365)): AppDate {
        const currHour = PaymentDateService.hour
        const currMinute = PaymentDateService.minute

        let testDate = appDate
        while (this._validatePmtDate(testDate, currHour, cutoffHour, currMinute, cutoffMinute) !== PaymentDateValidation.OK) {
            testDate = testDate.addDays(1)
            if (testDate.isAfter(max)) return null
        }
        return testDate
    }

    getNextValidPmtDate(appDate: AppDate, forSchedule: "for-schedule" | "for-one-time" | boolean): Promise<AppDate> {

        const cutoffHour = (forSchedule === true || forSchedule === "for-schedule") ?
            this.appService.appConfig.CUTOFF_HOUR_SCHEDULE :
            this.appService.appConfig.CUTOFF_HOUR
        const cutoffMinute = (forSchedule === true || forSchedule === "for-schedule") ?
            this.appService.appConfig.CUTOFF_MINUTE_SCHEDULE :
            this.appService.appConfig.CUTOFF_MINUTE

        const yearsToLoad = [
            appDate.year - 1,
            appDate.year,
            appDate.year + 1];

        return this.updateHolidays(yearsToLoad).then(_ => {
            return this.getNextValidDate(appDate, cutoffHour, cutoffMinute)
            // get next valid date
        })
    }

    getNextValidDateForPaymentSchedules(appDate: AppDate, max: AppDate = appDate.addDays(365)): AppDate {

        const cutoffHour = this.appService.appConfig.CUTOFF_HOUR_SCHEDULE;
        const currHour = PaymentDateService.hour
        const cutoffMinute = this.appService.appConfig.CUTOFF_MINUTE_SCHEDULE;
        const currMinute = PaymentDateService.minute
        let testDate = appDate
        let pmtDateValidation = this._validatePmtDate(testDate, currHour, cutoffHour, currMinute, cutoffMinute);
        while (pmtDateValidation !== PaymentDateValidation.OK && pmtDateValidation !== PaymentDateValidation.PAST_DATE) {
            // if some fatal error in validation - do what?
            if (pmtDateValidation as PaymentDateValidation === PaymentDateValidation.PAST_DATE
                || pmtDateValidation as PaymentDateValidation === PaymentDateValidation.ERROR) {
                // do some fatal error stuff? or return null?
                return null;
            }

            testDate = testDate.subtractDays(1);
            pmtDateValidation = this._validatePmtDate(testDate, currHour, cutoffHour, currMinute, cutoffMinute);
        }

        if (testDate.isAfter(max)) return null

        return testDate
    }

    getLastValidDate(appDate: AppDate, cutoffHour: number, cutoffMinute: number, min: AppDate): AppDate {
        const currHour = PaymentDateService.hour
        const currMinute = PaymentDateService.minute
        todo("what's happening here?")
        let testDate = appDate
        while (this._validatePmtDate(testDate, currHour, cutoffHour, currMinute, cutoffMinute) !== PaymentDateValidation.OK) {
            testDate = testDate.addDays(-1)
            if (testDate.isBefore(min)) return null
        }
        return testDate
    }

    /** refreshes and queries */
    getLastValidPmtDate(appDate: AppDate, forSchedule: "for-schedule" | "for-one-time" | boolean, min: AppDate): Observable<AppDate> {
        const cutoffHour = (forSchedule === true || forSchedule === "for-schedule") ?
            this.appService.appConfig.CUTOFF_HOUR_SCHEDULE :
            this.appService.appConfig.CUTOFF_HOUR
        const cutoffMinute = (forSchedule === true || forSchedule === "for-schedule") ?
            this.appService.appConfig.CUTOFF_MINUTE_SCHEDULE :
            this.appService.appConfig.CUTOFF_MINUTE

        return new Observable<AppDate>(observer=> {
            this.updateHolidays([appDate.year, appDate.year - 1]).then(() => {
                observer.next(this.getLastValidDate(appDate, cutoffHour, cutoffMinute, min))
            })
        })
    }

    /** is after today or today */
    isFutureDate(appDate: AppDate): boolean {
        return appDate instanceof AppDate && !appDate.isBefore(AppDate.now())
    }
}

export const genericDateValidationMessage = "Please enter a valid payment date."

export enum PaymentDateValidation {
    WEEKEND = 'weekend',
    HOLIDAY = 'holiday',
    PAST_DATE = 'past date',
    PAST_CUTOFF = 'past cutoff',
    ERROR = 'error',
    OK = 'ok'
}

/** Used in displaying to the user interface */
export const PaymentDateValidationMessages = {
    [PaymentDateValidation.WEEKEND]: "Payments cannot be processed on the weekend.",
    [PaymentDateValidation.HOLIDAY]: "Payments cannot be processed on a holiday.",
    [PaymentDateValidation.PAST_DATE]: "This date occurs in the past.",
    [PaymentDateValidation.PAST_CUTOFF]: "It is past the cutoff time for processing payments today.",
}

